import React, { useState, useEffect } from 'react';
import { Form, Button, Space, message, Spin, Select, Descriptions, Typography, Divider, Row, Col, Switch, Tag } from 'antd';
import { EditOutlined, GlobalOutlined, ColumnWidthOutlined, DollarOutlined, SafetyOutlined, TagOutlined } from '@ant-design/icons';
import CurrencySelect from '@components/shared/selects/CurrencySelect';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import { useTranslation } from 'react-i18next';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';

import { operatorApiFetch } from '@services/operator/apiService';
import { useIsStandard } from '@contexts/operator/PlanContext';

const LOCALE_OPTIONS = [
  { value: 'zh', label: '繁體中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
];

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Taipei', label: '(GMT+08:00) Taipei' },
  { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo' },
  { value: 'Asia/Seoul', label: '(GMT+09:00) Seoul' },
  { value: 'Asia/Shanghai', label: '(GMT+08:00) Shanghai' },
  { value: 'Asia/Hong_Kong', label: '(GMT+08:00) Hong Kong' },
  { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore' },
  { value: 'Asia/Bangkok', label: '(GMT+07:00) Bangkok' },
  { value: 'Asia/Jakarta', label: '(GMT+07:00) Jakarta' },
  { value: 'Asia/Kuala_Lumpur', label: '(GMT+08:00) Kuala Lumpur' },
  { value: 'Asia/Manila', label: '(GMT+08:00) Manila' },
  { value: 'America/Los_Angeles', label: '(GMT-08:00) Los Angeles' },
  { value: 'America/New_York', label: '(GMT-05:00) New York' },
  { value: 'America/Chicago', label: '(GMT-06:00) Chicago' },
  { value: 'Europe/London', label: '(GMT+00:00) London' },
  { value: 'Europe/Paris', label: '(GMT+01:00) Paris' },
  { value: 'Europe/Berlin', label: '(GMT+01:00) Berlin' },
  { value: 'Australia/Sydney', label: '(GMT+11:00) Sydney' },
  { value: 'Pacific/Auckland', label: '(GMT+13:00) Auckland' },
];

const OrganizationSettings = () => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const isStandard = useIsStandard();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const response = await operatorApiFetch('organizations/settings');
      const data = await response.json();

      if (data.success && data.data) {
        setOrganization(data.data);
        form.setFieldsValue({
          timezone: data.data.timezone,
          locale: data.data.locale,
          default_currency: data.data.default_currency,
          length_unit: data.data.length_unit?.toLowerCase(),
          weight_unit: data.data.weight_unit?.toLowerCase(),
          volume_unit: data.data.volume_unit?.toLowerCase(),
          admin_totp_required: data.data.admin_totp_required || false,
          operator_totp_required: data.data.operator_totp_required || false,
        });
      } else {
        throw new Error('Failed to fetch organization');
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setSaving(true);
      const response = await operatorApiFetch('organizations/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization: values }),
      });

      const data = await response.json();

      if (data.success) {
        message.success(t('common.message.update_success'));
        setOrganization({ ...organization, ...data.data });
        setEditMode(false);
      } else {
        throw new Error(data.message || 'Failed to update organization');
      }
    } catch (error) {
      console.error('Failed to update organization:', error);
      message.error(t('common.error.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.setFieldsValue({
      timezone: organization.timezone,
      locale: organization.locale,
      default_currency: organization.default_currency,
      length_unit: organization.length_unit?.toLowerCase(),
      weight_unit: organization.weight_unit?.toLowerCase(),
      volume_unit: organization.volume_unit?.toLowerCase(),
      admin_totp_required: organization.admin_totp_required || false,
      operator_totp_required: organization.operator_totp_required || false,
    });
    setEditMode(false);
  };

  if (loading || !organization) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <ListPageHeader
        items={['org_management', 'org_management.organization']}
        title={organization.name}
        actions={
          organization.editable && !editMode ? (
            <Can requiredPermission="organizations:write">
              <Button type="primary" icon={<EditOutlined />} onClick={() => setEditMode(true)}>
                {t('common.button.edit')}
              </Button>
            </Can>
          ) : undefined
        }
      />

      {editMode ? (
        <>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ maxWidth: 800 }}
          >
            <Form.Item
              name="timezone"
              label={<Space><GlobalOutlined />{t('operator.organization_settings.timezone')}</Space>}
              extra={t('operator.organization_settings.timezone_help')}
            >
              <Select
                placeholder={t('operator.organization_settings.timezone_placeholder')}
                allowClear showSearch optionFilterProp="label"
                options={TIMEZONE_OPTIONS} style={{ maxWidth: 400 }}
              />
            </Form.Item>

            <Form.Item
              name="locale"
              label={<Space><GlobalOutlined />{t('operator.organization_settings.locale')}</Space>}
              extra={t('operator.organization_settings.locale_help')}
            >
              <Select options={LOCALE_OPTIONS} style={{ maxWidth: 400 }} />
            </Form.Item>

            <Form.Item
              name="default_currency"
              label={<Space><DollarOutlined />{t('operator.organization_settings.default_currency')}</Space>}
              extra={t('operator.organization_settings.default_currency_help')}
            >
              <CurrencySelect apiFetch={operatorApiFetch} style={{ maxWidth: 400 }} />
            </Form.Item>

            <Divider orientation="left">
              <Space><ColumnWidthOutlined />{t('operator.organization_settings.unit_settings')}</Space>
            </Divider>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="length_unit" label={t('operator.organization_settings.length_unit')}>
                  <Select>
                    <Select.Option value="cm">{t('enums.organization.length_unit.cm')}</Select.Option>
                    <Select.Option value="in">{t('enums.organization.length_unit.in')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="weight_unit" label={t('operator.organization_settings.weight_unit')}>
                  <Select>
                    <Select.Option value="kg">{t('enums.organization.weight_unit.kg')}</Select.Option>
                    <Select.Option value="lb">{t('enums.organization.weight_unit.lb')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="volume_unit" label={t('operator.organization_settings.volume_unit')}>
                  <Select>
                    <Select.Option value="cbm">{t('enums.organization.volume_unit.cbm')}</Select.Option>
                    <Select.Option value="cuft">{t('enums.organization.volume_unit.cuft')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">
              <Space><SafetyOutlined />{t('operator.organization_settings.totp_policy')}</Space>
            </Divider>

            <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
              {t('operator.organization_settings.totp_policy_help')}
            </Typography.Paragraph>

            {!isStandard && (
              <Form.Item
                name="admin_totp_required"
                label={t('operator.organization_settings.merchant_totp_required')}
                valuePropName="checked"
                extra={t('operator.organization_settings.merchant_totp_required_help')}
              >
                <Switch />
              </Form.Item>
            )}

            <Form.Item
              name="operator_totp_required"
              label={t('operator.organization_settings.operator_totp_required')}
              valuePropName="checked"
              extra={t('operator.organization_settings.operator_totp_required_help')}
            >
              <Switch />
            </Form.Item>

            <FloatingActionBarSpacer />
          </Form>

          <FloatingActionBar
            role="operator"
            onCancel={handleCancel}
            onSubmit={() => form.submit()}
            loading={saving}
          />
        </>
      ) : (
        <>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={<Space><TagOutlined />{t('operator.organization_settings.workspace')}</Space>}>
              {organization.workspace ? (
                <Typography.Text code copyable>{organization.workspace}</Typography.Text>
              ) : (
                <Typography.Text type="secondary">{t('common.not_set')}</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={<Space><GlobalOutlined />{t('operator.organization_settings.timezone')}</Space>}>
              {organization.timezone || <Typography.Text type="secondary">{t('common.not_set')}</Typography.Text>}
            </Descriptions.Item>
            <Descriptions.Item label={<Space><GlobalOutlined />{t('operator.organization_settings.locale')}</Space>}>
              {organization.locale ? LOCALE_OPTIONS.find(o => o.value === organization.locale)?.label || organization.locale : <Typography.Text type="secondary">{t('common.not_set')}</Typography.Text>}
            </Descriptions.Item>
            <Descriptions.Item label={<Space><DollarOutlined />{t('operator.organization_settings.default_currency')}</Space>}>
              {organization.default_currency || <Typography.Text type="secondary">{t('common.not_set')}</Typography.Text>}
            </Descriptions.Item>
            <Descriptions.Item label={t('operator.organization_settings.plan')}>
              <Typography.Text strong>{t(`enums.organization.plan.${organization.plan}`)}</Typography.Text>
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" style={{ marginTop: 24 }}>
            <Space><ColumnWidthOutlined />{t('operator.organization_settings.unit_settings')}</Space>
          </Divider>

          <Descriptions bordered column={3}>
            <Descriptions.Item label={t('operator.organization_settings.length_unit')}>
              {organization.length_unit ? t(`enums.organization.length_unit.${organization.length_unit.toLowerCase()}`) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('operator.organization_settings.weight_unit')}>
              {organization.weight_unit ? t(`enums.organization.weight_unit.${organization.weight_unit.toLowerCase()}`) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('operator.organization_settings.volume_unit')}>
              {organization.volume_unit ? t(`enums.organization.volume_unit.${organization.volume_unit.toLowerCase()}`) : '-'}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left" style={{ marginTop: 24 }}>
            <Space><SafetyOutlined />{t('operator.organization_settings.totp_policy')}</Space>
          </Divider>

          <Descriptions bordered column={1}>
            {!isStandard && (
              <Descriptions.Item label={t('operator.organization_settings.merchant_totp_required')}>
                {organization.admin_totp_required
                  ? <Tag color="green">{t('operator.organization_settings.totp_enabled')}</Tag>
                  : <Tag>{t('operator.organization_settings.totp_disabled')}</Tag>
                }
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('operator.organization_settings.operator_totp_required')}>
              {organization.operator_totp_required
                ? <Tag color="green">{t('operator.organization_settings.totp_enabled')}</Tag>
                : <Tag>{t('operator.organization_settings.totp_disabled')}</Tag>
              }
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </div>
  );
};

export default OrganizationSettings;
